import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { CartNotification } from '~/components/Header/components/CartNotification';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { useModal } from '~/hooks/useModal';
import useNavigateCart from '~/hooks/useNavigateCart';

import { cartStore, userStore } from '~/stores';

import Icon from '../../Icon/Icon';
import { ModalType } from '../../Modal/interface';
import ModalCart from '../../ModalCart/ModalCart';

import styles from './NavButtons.module.scss';

export const NavButtons = observer(() => {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const location = useLocation();
  const [showCartModal, setShowCartModal] = useState(false);

  const handleNavigateCart = useNavigateCart({
    beforeNavigate: () => {
      if (showCartModal) {
        setShowCartModal(false);
      }
    },
    source: 'NavButtons',
  });

  const handleAuth = (e: unknown) => {
    if (userStore.isAuthorized) {
      return;
    }
    (e as MouseEvent).preventDefault();
    openModal(ModalType.Auth, 'header', {
      redirect: company.match({
        citydrinks: '/cabinet',
        default: '/cabinet/personal-data',
      }),
    });
  };

  const handleOrders = (e: unknown) => {
    if (userStore.isAuthorized) {
      return;
    }
    (e as MouseEvent).preventDefault();
    openModal(ModalType.Auth, 'header', {
      redirect: company.match({
        default: '/cabinet/orders',
      }),
    });
  };

  const icons: Record<string, IconTypes> = company.match({
    circlek: {
      profile: 'menu-personal',
      orders: 'orders',
      fav: 'nav-bar-fav',
      cart: 'bag',
    },
    default: {
      profile: 'profile-new',
      orders: 'orders',
      fav: 'fav',
      cart: 'bag',
    },
  });

  if (DEFAULT_COMPANIES.includes(company.name)) {
    return (
      <div className={styles.header_btn_group} data-company={company.name}>
        <Link
          data-link="header-cabinet"
          to={company.match({
            citydrinks: '/cabinet',
            default: '/cabinet/personal-data',
          })}
          className={styles.header_btn}
          onClick={handleAuth}
        >
          <Icon
            type={icons.profile as IconTypes}
            size={24}
            className={styles.header_btn__icon}
          />
          <div className={styles.header_btn__text}>
            {userStore.isAuthorized ? t('account') : t('logIn')}
          </div>
        </Link>
        <Link
          data-link="header-orders"
          to="/cabinet/orders"
          className={styles.header_btn}
          onClick={handleOrders}
        >
          <Icon
            type={icons.orders as IconTypes}
            size={24}
            className={styles.header_btn__icon}
          />
          <div className={styles.header_btn__text}>{t('orders')}</div>
        </Link>
        <Link data-link="header-favs" to="/favs" className={styles.header_btn}>
          <Icon
            type={icons.fav as IconTypes}
            size={24}
            className={styles.header_btn__icon}
          />
          <div className={styles.header_btn__text}>{t('favs')}</div>
        </Link>
        <div
          data-button="header-cart"
          className={styles.header_btn}
          onMouseEnter={() =>
            cartStore.totalCartCount > 0 &&
            !['/cart', '/checkout'].includes(location.pathname) &&
            setShowCartModal(true)
          }
          onMouseLeave={() => setShowCartModal(false)}
          onClick={handleNavigateCart}
        >
          <Icon
            type={icons.cart as IconTypes}
            size={24}
            className={styles.header_btn__icon}
          />
          {cartStore.totalCartCount > 0 && (
            <div className={styles.header_btn__count}>
              {cartStore.totalCartCount}
            </div>
          )}
          <div className={styles.header_btn__text}>{t('cart')}</div>
          {showCartModal && !!cartStore.cart.length && (
            <ModalCart onClose={() => setShowCartModal(false)} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.header_btn_group} data-company={company.name}>
      <Link data-link="header-favs" to="/favs" className={styles.header_btn}>
        <Icon type="fav" size={24} className={styles.header_btn__icon} />
        <div className={styles.header_btn__text}>{t('favs')}</div>
      </Link>
      <Link
        data-link="header-cabinet"
        to="/cabinet"
        className={styles.header_btn}
        onClick={handleAuth}
      >
        <Icon type="profile" size={24} className={styles.header_btn__icon} />
        <div className={styles.header_btn__text}>
          {t(userStore.isAuthorized ? 'cabinet' : 'logIn')}
        </div>
      </Link>
      <div
        data-button="header-checkout"
        className={styles.header_btn}
        onMouseLeave={() => setShowCartModal(false)}
        onClick={handleNavigateCart}
      >
        <div
          data-button="header-cart"
          onMouseEnter={() =>
            cartStore.totalCartCount > 0 &&
            !['/cart', '/checkout'].includes(location.pathname) &&
            setShowCartModal(true)
          }
        >
          <Icon type="bag" size={24} className={styles.header_btn__icon} />
          {cartStore.totalCartCount > 0 && (
            <div className={styles.header_btn__count}>
              {cartStore.totalCartCount}
            </div>
          )}
        </div>
        <div className={styles.header_btn__text}>{t('cart')}</div>
        {showCartModal && !!cartStore.cart.length && (
          <ModalCart onClose={() => setShowCartModal(false)} />
        )}
        <CartNotification />
      </div>
    </div>
  );
});
